import React from 'react';
import gql from 'graphql-tag';
import { useTranslation, Trans } from 'react-i18next';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { useIsMounted } from '../../hooks/useIsMounted';

import { WeDidItFragment } from '../../../types/__generated__';
import { useTracking } from '@justgiving/react-tracking';
import Svg from '@justgiving/svg';
import { timerIcon } from '@justgiving/icons';
import WeDidItConfetti from './WeDidItConfetti';
import parseCurrency from '../../utils/parseCurrency';

import './WeDidItHeader.scss';
import { filterByTypename } from '../../utils/graphql';
import FacebookShare from '../SocialButtons/FacebookShare';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import { PageMetaContext } from '../../context/PageMetaContext';
import generateHash from '../../utils/generateHash';

const FRAGMENT = buildQuery(gql`
  fragment WeDidIt on Page {
    endDate
    donationSummary {
      totalAmount {
        currencyCode
        value
      }
    }
    donations(first: 1) {
      totalCount
    }
  }
`);

const WeDidItHeader: React.FunctionComponent = () => {
  const result = usePageQuery<WeDidItFragment>(FRAGMENT);
  const basePageData = useBasePageQuery();
  const { pageType, pageShortName, currentUrl } = useNotNullableContext(
    PageMetaContext,
  );
  const { t } = useTranslation('common', { useSuspense: false });
  const track = useTracking();
  const isMounted = useIsMounted();
  if (!isMounted) {
    return null;
  }

  if (!basePageData.data?.page || !UsePageQueryResult.hasData(result)) {
    return null;
  }

  const { endDate, donationSummary, donations } = result.data;
  const { relationships } = basePageData.data.page;

  const donationCurrency = donationSummary?.totalAmount?.currencyCode ?? 'GBP';
  const donationTotalAmount = donationSummary?.totalAmount?.value / 100 ?? 0;
  const donationTotalCount = donations && donations.totalCount;
  const campaignEndDate = new Date(endDate).getTime();
  const campaignEndDateString = campaignEndDate.toString();
  const formattedEndDate = t('campaign.closed', { campaignEndDateString });
  const totalDonations =
    donationCurrency && parseCurrency(donationCurrency, donationTotalAmount);
  const beneficiaries = relationships?.beneficiaries;
  const charity =
    beneficiaries?.nodes && filterByTypename(beneficiaries?.nodes, 'Charity');
  const charityUrl = charity && charity[0]?.profilePageUrl;

  // converting toDateString to remove time of day from time stamp - allows easier comparison in isCampaignExpired
  const nowDateFormat = new Date().toDateString();
  const campaignDateFormat = new Date(campaignEndDate).toDateString();

  const isCampaignExpired = () => {
    const isExpired =
      new Date(nowDateFormat).getTime() >
      new Date(campaignDateFormat).getTime();
    return isExpired;
  };

  if (!campaignEndDate || !isCampaignExpired()) {
    return null;
  }

  return (
    <div className="jg-background--white-smoke jg-space-pbsm jg-text--center jg-campaigns-expired-bg-container">
      <div className="jg-campaigns-expired-bg jg-fill--primary jg-campaigns-expired-bg--svg">
        <WeDidItConfetti />
      </div>
      <div className="row jg-space-ptlg">
        <div className="col-lg-8 col-xl-6 jg-space-ma">
          <p className="jg-h1">{t('campaign.weDidIt')}</p>
          <p className="jg-text--light">
            <Trans i18nKey="campaign.complete">
              Our campaign is now complete.{' '}
              <span className="jg-text">
                {{ donationTotalCount }} supporters
              </span>{' '}
              helped us raise{' '}
            </Trans>
            <span className="jg-text-color-branded">{totalDonations}</span>
          </p>
          <FacebookShare
            url={`${currentUrl}?utm_source=facebook&utm_medium=${pageType}&utm_content=${pageShortName}&utm_campaign=pfp-share&utm_term=${generateHash()}`}
            trackingData={{ page_section: 'we did it' }}
            buttonText={t('campaign.inspireShare')}
          />
          <div className="jg-space-mtsm">
            <a
              className="jg-btn jg-btn--ghost"
              onClick={() =>
                track({
                  analytics: {
                    event: 'link click',
                    subtype: 'button',
                    event_value: 'view charity profile',
                    page_section: 'we did it',
                  },
                })
              }
              href={charityUrl}
            >
              {t('campaign.visitCharityProfile')}
            </a>
          </div>
          <div className="jg-space-mtms">
            <Svg
              className="jg-icon jg-fill--nobel jg-space-ma jg-display-ib"
              markup={timerIcon}
            />{' '}
            <div className="jg-color--nobel jg-display-ib">
              {formattedEndDate}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeDidItHeader;
